import { FormArray, FormControl, FormGroup, Validators } from "@angular/forms";
import { ServiceFormItem, ServiceItemForm } from "./item/item.model";
import { FormType } from "../master-data/master-data.model";
import { TableItem, TableItemForm } from "./table-field/table-field.model";
import { ServiceChart, ServiceChartForm } from "./chart/chart.model";
import { LibraryDocument } from "../library/library.model";
import {
  ActionType,
  PermissionStringMap,
} from "../forms-permissions/forms.model";

export class FormTemplate {
  uuid?: string | null;
  title: string;
  groups: ServiceSection[];
  fields: ServiceFormItem[];
  tables: ServiceTable[];
  signatures?: ServiceSignature[];
  attachments?: ServiceAttachment[];
  attachmentGroups?: ServiceAttachmentGroup[];
  interactiveAttachmentGroups?: InteractiveAttachmentGroup[];
  deviceAttachmentGroups?: DeviceAttachmentGroup[];
  drawings?: ServiceDrawing[];
  documents?: FormDocument[];
  templateBase64?: string;
  active: boolean;
  formType?: FormType;
  allObjects: boolean;
  signaturePerUser: boolean;
  multiFormAllowed: boolean;
  additionalServicemanAllowed: boolean;
  sendByEmail: boolean;
  orderRelated: boolean;
  attachmentOnly: boolean;
  permissions: Map<ActionType, PermissionStringMap>;
  isSurvey?: boolean  ;

  constructor(
    title?: string,
    groups?: ServiceSection[],
    fields?: ServiceFormItem[],
    tables?: ServiceTable[],
    signatures?: ServiceSignature[],
    attachments?: ServiceAttachment[],
    attachmentGroups?: ServiceAttachmentGroup[],
    interactiveAttachmentGroups?: InteractiveAttachmentGroup[],
    deviceAttachmentGroups?: DeviceAttachmentGroup[],
    drawings?: ServiceDrawing[],
    documents?: FormDocument[],
    uuid?: string,
    active = true,
    formType: FormType = null,
    allObjects = false,
    signaturePerUser = false,
    multiFormAllowed = true,
    additionalServicemanAllowed = true,
    sendByEmail = false,
    attachmentOnly = false,
    permissions?: Map<ActionType, PermissionStringMap>,
    isSurvey?: boolean
  ) {
    this.uuid = uuid;
    this.title = title;
    this.groups = groups;
    this.fields = fields;
    this.tables = tables;
    this.signatures = signatures;
    this.attachments = attachments;
    this.attachmentGroups = attachmentGroups;
    this.interactiveAttachmentGroups = interactiveAttachmentGroups;
    this.deviceAttachmentGroups = deviceAttachmentGroups;
    this.drawings = drawings;
    this.documents = documents;
    this.active = active;
    this.formType = formType;
    this.allObjects = allObjects;
    this.signaturePerUser = signaturePerUser;
    this.multiFormAllowed = multiFormAllowed;
    this.additionalServicemanAllowed = additionalServicemanAllowed;
    this.sendByEmail = sendByEmail;
    this.attachmentOnly = attachmentOnly;
    this.permissions = permissions;
    this.isSurvey = isSurvey
  }

  toMap(form: FormTemplate): Map<string, unknown> {
    const map = new Map<string, unknown>();
    map.set("title", form.title);
    map.set("active", form.active ?? false);
    map.set("allObjects", form.allObjects ?? false);
    map.set("signaturePerUser", form.signaturePerUser ?? false);
    map.set("multiFormAllowed", form.multiFormAllowed ?? false);
    map.set(
      "additionalServicemanAllowed",
      form.additionalServicemanAllowed ?? false,
    );
    map.set("sendByEmail", form.sendByEmail ?? false);
    map.set("attachmentOnly", form.attachmentOnly ?? false);
    if (form.formType != null) {
      const fType = new FormType();
      Object.assign(fType, form.formType);
      map.set("formType", fType.toMap(fType));
    }
    if (form.groups?.length > 0) {
      map.set(
        "groups",
        form.groups.map((s) => {
          const sec = new ServiceSection(s.name, s.fields, s.sort);
          return sec.toMap(sec);
        }),
      );
    }

    if (form.fields?.length > 0) {
      map.set(
        "fields",
        form.fields.map((f) => {
          const fld = new ServiceFormItem();
          Object.assign(fld, f);
          return fld.toMap(fld);
        }),
      );
    }

    if (form.tables?.length > 0) {
      map.set(
        "tables",
        form.tables.map((t) => {
          const tab = new ServiceTable(
            t.name,
            t.type,
            t.columns,
            t.renderTag,
            t.sort,
            t.charts,
          );
          return tab.toMap(tab);
        }),
      );
    }
    if (form.signatures?.length > 0) {
      map.set(
        "signatures",
        form.signatures.map((a) => {
          const sign = new ServiceSignature(
            a.name,
            a.renderTag,
            a.optional,
            a.sort,
            a.withInstallationPicture,
          );
          return sign.toMap(sign);
        }),
      );
    }
    if (form.attachments?.length > 0) {
      map.set(
        "attachments",
        form.attachments.map((a) => {
          const att = new ServiceAttachment(
            a.name,
            a.renderTag,
            a.templateUuid,
            a.newPage,
            a.optional,
            a.filled,
            a.sort,
          );
          return att.toMap(att);
        }),
      );
    }
    if (form.drawings?.length > 0) {
      map.set(
        "drawings",
        form.drawings.map((a) => {
          const draw = new ServiceDrawing(
            a.name,
            a.renderTag,
            a.blankBkg,
            a.base64PngBackground,
            a.optional,
            a.sort,
          );
          return draw.toMap(draw);
        }),
      );
    }
    if (form.attachmentGroups?.length > 0) {
      map.set(
        "attachmentGroups",
        form.attachmentGroups.map((s) => {
          const sec = new ServiceAttachmentGroup(
            s.name,
            s.renderTag,
            s.type,
            s.sort,
            s.attachments,
          );
          return sec.toMap(sec);
        }),
      );
    }
    if (form.interactiveAttachmentGroups?.length > 0) {
      map.set(
        "interactiveAttachmentGroups",
        form.interactiveAttachmentGroups.map((s) => {
          const sec = new InteractiveAttachmentGroup(
            s.name,
            s.renderTag,
            s.minNoOfAtt,
            s.maxNoOfAtt,
            s.sort,
            s.attachments,
          );
          return sec.toMap(sec);
        }),
      );
    }

    if (form.deviceAttachmentGroups?.length > 0) {
      map.set(
        "deviceAttachmentGroups",
        form.deviceAttachmentGroups.map((s) => {
          const sec = new DeviceAttachmentGroup(
            s.name,
            s.renderTag,
            s.type,
            s.sort,
            s.attachments,
          );
          return sec.toMap(sec);
        }),
      );
    }
    if (form.documents?.length > 0) {
      map.set(
        "documents",
        form.documents.map((d) => {
          const doc = new FormDocument(d.name, d.document, d.sort);
          return doc.toMap(doc);
        }),
      );
    }
    if (form.permissions) {
      map.set("permissions", form.permissions);
    }
    map.set("isSurvey", form.isSurvey ?? false);
    return map;
  }
}

export class TemplateFormForm {
  title = new FormControl(null);
  groups = new FormArray([]);
  fields = new FormArray([]);
  tables = new FormArray([]);
  signatures = new FormArray([]);
  attachments = new FormArray([]);
  attachmentGroups = new FormArray([]);
  interactiveAttachmentGroups = new FormArray([]);
  deviceAttachmentGroups = new FormArray([]);
  drawings = new FormArray([]);
  documents = new FormArray([]);
  statuses = new FormControl([]);
  allStatuses = new FormControl(true);
  departments = new FormControl([]);
  allDepartments = new FormControl(true);
  active = new FormControl(true);
  formType = new FormControl(null);
  allObjects = new FormControl(false);
  signaturePerUser = new FormControl(false);
  multiFormAllowed = new FormControl(true);
  additionalServicemanAllowed = new FormControl(false);
  sendByEmail = new FormControl(false);
  attachmentOnly = new FormControl(false);
  orderRelated = new FormControl(true);
  permissions = new FormControl(null);
  isSurvey = new FormControl(false);

  constructor(data: FormTemplate) {
    if (data.title) {
      this.title.setValue(data.title);
    }
    if (data.active === true) {
      this.active.setValue(true);
    } else {
      this.active.setValue(false);
    }
    this.formType.setValue(data.formType);
    if (data.allObjects === true) {
      this.allObjects.setValue(true);
    }
    if (data.signaturePerUser === true) {
      this.signaturePerUser.setValue(true);
    } else {
      this.signaturePerUser.setValue(false);
    }
    if (data.multiFormAllowed === true) {
      this.multiFormAllowed.setValue(true);
    } else {
      this.multiFormAllowed.setValue(false);
    }
    if (data.additionalServicemanAllowed === true) {
      this.additionalServicemanAllowed.setValue(true);
    } else {
      this.additionalServicemanAllowed.setValue(false);
    }
    if (data.sendByEmail === true) {
      this.sendByEmail.setValue(true);
    } else {
      this.sendByEmail.setValue(false);
    }
    if (data.attachmentOnly === true) {
      this.attachmentOnly.setValue(true);
    } else {
      this.attachmentOnly.setValue(false);
    }
    if (data.orderRelated || data.orderRelated == null) {
      this.orderRelated.setValue(true);
    } else {
      this.orderRelated.setValue(false);
    }
    this.title.setValidators([Validators.required]);

    if (data.fields) {
      if (data.fields.length > 1) {
        data.fields.sort((a, b) =>
          a.sort > b.sort ? 1 : a.sort < b.sort ? -1 : 0,
        );
      }
      data.fields.forEach((f) => {
        this.fields.push(new FormGroup(new ServiceItemForm(f)));
      });
    }

    if (data.groups) {
      if (data.groups.length > 1) {
        data.groups.sort((a, b) =>
          a.sort > b.sort ? 1 : a.sort < b.sort ? -1 : 0,
        );
      }

      data.groups.forEach((gr) => {
        this.groups.push(
          new FormGroup(
            new ServiceSectionForm(
              new ServiceSection(
                gr.name,
                gr.fields ? gr.fields : [],
                gr.sort ? gr.sort : null,
              ),
            ),
          ),
        );
      });
    }
    if (data.tables) {
      if (data.tables.length > 1) {
        data.tables.sort((a, b) =>
          a.sort > b.sort ? 1 : a.sort < b.sort ? -1 : 0,
        );
      }
      data.tables.forEach((f) => {
        this.tables.push(new FormGroup(new ServiceTableForm(f)));
      });
    }
    if (data.signatures) {
      if (data.signatures.length > 1) {
        data.signatures.sort((a, b) =>
          a.sort > b.sort ? 1 : a.sort < b.sort ? -1 : 0,
        );
      }
      data.signatures.forEach((f) => {
        this.signatures.push(new FormGroup(new ServiceSignatureForm(f)));
      });
    }
    if (data.drawings) {
      data.drawings.forEach((f) => {
        this.drawings.push(new FormGroup(new ServiceDrawingForm(f)));
      });
    }
    if (data.attachments) {
      data.attachments.forEach((f) => {
        this.attachments.push(new FormGroup(new ServiceAttachmentForm(f)));
      });
    }
    if (data.attachmentGroups) {
      if (data.attachmentGroups.length > 1) {
        data.attachmentGroups.sort((a, b) =>
          a.sort > b.sort ? 1 : a.sort < b.sort ? -1 : 0,
        );
      }

      data.attachmentGroups.forEach((attGroup) => {
        this.attachmentGroups.push(
          new FormGroup(
            new ServiceAttachmentGroupForm(
              new ServiceAttachmentGroup(
                attGroup.name,
                attGroup.renderTag,
                attGroup.type,
                attGroup.sort ? attGroup.sort : null,
                attGroup.attachments,
              ),
            ),
          ),
        );
      });
    }
    if (data.interactiveAttachmentGroups) {
      if (data.interactiveAttachmentGroups.length > 1) {
        data.interactiveAttachmentGroups.sort((a, b) =>
          a.sort > b.sort ? 1 : a.sort < b.sort ? -1 : 0,
        );
      }

      data.interactiveAttachmentGroups.forEach((attGroup) => {
        this.interactiveAttachmentGroups.push(
          new FormGroup(
            new InteractiveAttachmentGroupForm(
              new InteractiveAttachmentGroup(
                attGroup.name,
                attGroup.renderTag,
                attGroup.minNoOfAtt,
                attGroup.maxNoOfAtt,
                attGroup.sort ? attGroup.sort : null,
                attGroup.attachments,
              ),
            ),
          ),
        );
      });
    }
    if (data.deviceAttachmentGroups) {
      if (data.deviceAttachmentGroups.length > 1) {
        data.deviceAttachmentGroups.sort((a, b) =>
          a.sort > b.sort ? 1 : a.sort < b.sort ? -1 : 0,
        );
      }

      data.deviceAttachmentGroups.forEach((attGroup) => {
        this.deviceAttachmentGroups.push(
          new FormGroup(
            new DeviceAttachmentGroupForm(
              new DeviceAttachmentGroup(
                attGroup.name,
                attGroup.renderTag,
                attGroup.type,
                attGroup.sort ? attGroup.sort : null,
                attGroup.attachments,
              ),
            ),
          ),
        );
      });
    }
    if (data.documents) {
      data.documents.forEach((d) => {
        this.documents.push(new FormGroup(new FormDocumentForm(d)));
      });
    }
    if (data.permissions) {
      this.permissions.setValue(data.permissions);
    }
    if (data.isSurvey === true) {
      this.isSurvey.setValue(true);
    } else {
      this.isSurvey.setValue(false);
    }
  }
}

export class ServiceSection {
  name: string;
  fields?: ServiceFormItem[];
  sort?: number;

  constructor(name: string, fields?: ServiceFormItem[], sort?: number) {
    this.name = name;
    this.fields = fields && [...fields];
    this.sort = sort;
  }

  toMap(sec: ServiceSection): Map<string, unknown> {
    const map = new Map<string, unknown>();
    map.set("name", sec.name);
    map.set("sort", sec.sort ?? 0);
    if (sec.fields?.length > 0) {
      const fields = [];
      sec.fields.forEach((f) => {
        const si = new ServiceFormItem();
        Object.assign(si, f);
        fields.push(si.toMap(si));
      });
      map.set("fields", fields);
    }
    return map;
  }
}

export class ServiceSectionForm {
  name = new FormControl();
  sort = new FormControl();
  fields = new FormArray([], [Validators.required]);

  constructor(section: ServiceSection) {
    if (section.name) {
      this.name.setValue(section.name);
    }
    if (section.sort != null) {
      this.sort.setValue(section.sort);
    } else {
      this.sort.setValue(0);
    }
    this.name.setValidators([Validators.required]);
    if (section.fields) {
      section.fields.forEach((f) => {
        this.fields.push(new FormGroup(new ServiceItemForm(f)));
      });
    }
  }
}

export class ServiceSignature {
  name: string;
  optional: boolean;
  renderTag: string;
  sort?: number;
  withInstallationPicture: boolean;

  constructor(
    name: string,
    tag?: string,
    optional?: boolean,
    sort?: number,
    withInstPicture?: boolean,
  ) {
    this.name = name;
    this.renderTag = tag;
    this.optional = optional ?? false;
    this.sort = sort ?? 0;
    this.withInstallationPicture = withInstPicture ?? false;
  }

  toMap(signature: ServiceSignature): Map<string, unknown> {
    const map = new Map<string, unknown>();
    map.set("name", signature.name);
    map.set("renderTag", signature.renderTag);
    map.set("optional", signature.optional ?? false);
    map.set("sort", signature.sort ?? 0);
    map.set(
      "withInstallationPicture",
      signature.withInstallationPicture ?? false,
    );

    return map;
  }
}

export class ServiceSignatureForm {
  name = new FormControl();
  optional = new FormControl();
  renderTag = new FormControl();
  sort = new FormControl();
  withInstallationPicture = new FormControl();

  constructor(signature: ServiceSignature) {
    if (signature.name) {
      this.name.setValue(signature.name);
    }
    if (signature.renderTag) {
      this.renderTag.setValue(signature.renderTag);
    }
    if (signature.optional) {
      this.optional.setValue(signature.optional);
    } else {
      this.optional.setValue(false);
    }
    if (signature.sort != null) {
      this.sort.setValue(signature.sort);
    } else {
      this.sort.setValue(0);
    }
    if (signature.withInstallationPicture != null) {
      this.withInstallationPicture.setValue(signature.withInstallationPicture);
    }

    this.name.setValidators([Validators.required]);
    this.name.updateValueAndValidity();
    this.renderTag.setValidators([Validators.required]);
    this.renderTag.updateValueAndValidity();
  }
}

export class ServiceDrawing {
  name: string;
  optional: boolean;
  renderTag: string;
  sort?: number;
  base64PngBackground: string;
  blankBkg?: boolean;

  constructor(
    name: string,
    tag: string,
    blankBkg = true,
    background?: string,
    optional?: boolean,
    sort?: number,
  ) {
    this.name = name;
    this.renderTag = tag;
    this.base64PngBackground = background ?? null;
    this.blankBkg = blankBkg;
    this.optional = optional ?? false;
    this.sort = sort ?? 0;
  }

  toMap(drawing: ServiceDrawing): Map<string, unknown> {
    const map = new Map<string, unknown>();
    map.set("name", drawing.name);
    map.set("renderTag", drawing.renderTag);
    map.set("blankBkg", drawing.blankBkg);
    map.set("base64PngBackground", drawing.base64PngBackground);
    map.set("optional", drawing.optional ?? false);
    map.set("sort", drawing.sort ?? 0);
    return map;
  }
}

export class ServiceDrawingForm {
  name = new FormControl();
  base64PngBackground = new FormControl();
  optional = new FormControl();
  renderTag = new FormControl();
  sort = new FormControl();
  blankBkg = new FormControl();

  constructor(drawing: ServiceDrawing) {
    if (drawing.name) {
      this.name.setValue(drawing.name);
    }
    if (drawing.renderTag) {
      this.renderTag.setValue(drawing.renderTag);
    }
    if (drawing.base64PngBackground) {
      this.base64PngBackground.setValue(drawing.base64PngBackground);
      this.blankBkg.setValue(false);
    } else {
      this.blankBkg.setValue(true);
      this.base64PngBackground.setValue(null);
    }
    if (drawing.optional) {
      this.optional.setValue(drawing.optional);
    } else {
      this.optional.setValue(false);
    }
    if (drawing.sort !== undefined) {
      this.sort.setValue(drawing.sort);
    }

    this.name.setValidators([Validators.required]);
    this.name.updateValueAndValidity();
    this.renderTag.setValidators([Validators.required]);
    this.renderTag.updateValueAndValidity();
  }
}

export enum FormTableType {
  BASIC = 0,
  DEVICES = 1,
  AGGREGATE = 2,
}

export class ServiceTable {
  name: string;
  columns: TableItem[];
  renderTag: string;
  sort: number;
  type: FormTableType;
  charts?: ServiceChart[];

  constructor(
    name: string,
    type: FormTableType,
    columns?: TableItem[],
    tag?: string,
    sort?: number,
    charts?: ServiceChart[],
  ) {
    this.name = name;
    this.renderTag = tag;
    this.columns = columns && [...columns];
    this.sort = sort ?? 0;
    this.type = type ?? FormTableType.BASIC;
    this.charts = charts && [...charts];
  }

  toMap(table: ServiceTable): Map<string, unknown> {
    const map = new Map<string, unknown>();
    map.set("name", table.name);
    map.set("type", table.type ?? 0);
    map.set("renderTag", table.renderTag);
    map.set("sort", table.sort ?? 0);
    if (table.charts?.length > 0) {
      const chartArray = [];
      table.charts.forEach((c) => {
        // always taken from the owning table, so a renamed table tag can't leave it stale
        const chart = new ServiceChart({ ...c, tableKey: table.renderTag });
        chartArray.push(chart.toMap(chart));
      });
      map.set("charts", chartArray);
    }
    if (table.columns?.length > 0) {
      const colArray = [];
      table.columns.forEach((c) => {
        const col = new TableItem(
          c.name,
          c.type,
          c.columnKey,
          c.sort,
          c.width,
          c.values,
          c.required,
          c.visible,
          c.defaultValue,
          c.aggregate,
          c.formTableAggregateType,
          c.valueMap
        );
        colArray.push(col.toMap(col));
      });
      map.set("columns", colArray);
    }
    return map;
  }
}

export class ServiceTableForm {
  name = new FormControl();
  renderTag = new FormControl();
  columns = new FormArray([], [Validators.required]);
  charts = new FormArray([]);
  sort = new FormControl();
  type = new FormControl();

  constructor(table: ServiceTable) {
    if (table.name) {
      this.name.setValue(table.name);
    }
    if (table.renderTag) {
      this.renderTag.setValue(table.renderTag);
    }
    if (table.columns) {
      table.columns.sort((a, b) =>
        a.sort > b.sort ? 1 : b.sort > a.sort ? -1 : 0,
      );
      table.columns.forEach((c) => {
        this.columns.push(new FormGroup(new TableItemForm(c) as unknown));
      });
    }
    if (table.charts) {
      [...table.charts]
        .sort((a, b) => ((a?.sort ?? 0) > (b?.sort ?? 0) ? 1 : -1))
        .forEach((c) => {
          this.charts.push(
            new FormGroup(new ServiceChartForm(c) as unknown) as never,
          );
        });
    }
    if (table.sort != null) {
      this.sort.setValue(table.sort);
    }
    if (table.type != null) {
      this.type.setValue(table.type);
    }
    this.name.setValidators(Validators.required);
    this.renderTag.setValidators(Validators.required);
    this.name.updateValueAndValidity();
    this.renderTag.updateValueAndValidity();
  }
}

export class ServiceAttachment {
  name: string;
  newPage: boolean;
  optional: boolean;
  renderTag: string;
  templateUuid: string;
  sort?: number;
  filled?: boolean;

  constructor(
    name: string,
    tag?: string,
    childTemplate?: string,
    newPage?: boolean,
    optional?: boolean,
    filled?: boolean,
    sort?: number,
  ) {
    this.name = name;
    this.renderTag = tag;
    this.templateUuid = childTemplate ?? null;
    this.newPage = newPage ?? false;
    this.optional = optional ?? false;
    this.filled = filled ?? false;
    this.sort = sort ?? 0;
  }

  toMap(attachment: ServiceAttachment): Map<string, unknown> {
    const map = new Map<string, unknown>();
    map.set("name", attachment.name);
    map.set("renderTag", attachment.renderTag);
    map.set("templateUuid", attachment.templateUuid);
    map.set("newPage", attachment.newPage ?? false);
    map.set("optional", attachment.optional ?? false);
    map.set("filled", attachment.filled ?? false);
    map.set("sort", attachment.sort ?? 0);

    return map;
  }
}
export class DeviceAttachment extends ServiceAttachment {
  productCategoryUuid: string;

  constructor(
    name: string,
    prodCategoryUuid: string,
    tag?: string,
    childTemplate?: string,
    newPage?: boolean,
    optional?: boolean,
    filled?: boolean,
    sort?: number,
  ) {
    super(name, tag, childTemplate, newPage, optional, filled, sort);
    this.productCategoryUuid = prodCategoryUuid;
  }

  toMap(attachment: DeviceAttachment): Map<string, unknown> {
    const mp = super.toMap(attachment);
    mp.set("productCategoryUuid", attachment.productCategoryUuid);
    return mp;
  }
}

export class ServiceAttachmentForm {
  name = new FormControl();
  newPage = new FormControl();
  optional = new FormControl();
  filled = new FormControl();
  renderTag = new FormControl();
  templateUuid = new FormControl();
  sort = new FormControl();

  constructor(attachment: ServiceAttachment) {
    if (attachment.name) {
      this.name.setValue(attachment.name);
    }
    if (attachment.templateUuid) {
      this.templateUuid.setValue(attachment.templateUuid);
    }
    if (attachment.renderTag) {
      this.renderTag.setValue(attachment.renderTag);
    }
    if (attachment.newPage) {
      this.newPage.setValue(attachment.newPage);
    } else {
      this.newPage.setValue(false);
    }
    if (attachment.optional) {
      this.optional.setValue(attachment.optional);
    } else {
      this.optional.setValue(false);
    }
    if (attachment.filled) {
      this.filled.setValue(attachment.filled);
    } else {
      this.filled.setValue(false);
    }
    if (attachment.sort !== undefined) {
      this.sort.setValue(attachment.sort);
    }

    this.name.setValidators([Validators.required]);
    this.name.updateValueAndValidity();
    this.templateUuid.setValidators([Validators.required]);
    this.templateUuid.updateValueAndValidity();
    this.renderTag.setValidators([Validators.required]);
    this.renderTag.updateValueAndValidity();
  }
}

export class DeviceAttachmentForm extends ServiceAttachmentForm {
  productCategoryUuid = new FormControl();

  constructor(attachment: DeviceAttachment) {
    super(attachment);
    if (attachment.productCategoryUuid) {
      this.productCategoryUuid.setValue(attachment.productCategoryUuid);
    }
    this.name.clearValidators();
    this.name.updateValueAndValidity();
    this.productCategoryUuid.setValidators([Validators.required]);
    this.productCategoryUuid.updateValueAndValidity();
  }
}

export enum ServiceAttachmentGroupType {
  REQUIRED_ONE = 0,
  REQUIRED_ONE_OR_MORE = 1,
  REQUIRED_ALL = 2,
  ALL_OPTIONAL = 3,
}

export class AttachmentGroup {
  name: string;
  renderTag: string;
  type: ServiceAttachmentGroupType;
  sort?: number;

  constructor(
    name: string,
    tag: string,
    type: ServiceAttachmentGroupType,
    sort?: number,
  ) {
    this.name = name;
    this.renderTag = tag;
    this.type = type;
    this.sort = sort ?? 0;
  }

  toMap(attachmentGroup: AttachmentGroup): Map<string, unknown> {
    const map = new Map<string, unknown>();
    map.set("name", attachmentGroup.name);
    map.set("renderTag", attachmentGroup.renderTag);
    map.set("type", attachmentGroup.type);
    map.set("sort", attachmentGroup.sort ?? 0);
    return map;
  }
}

export class ServiceAttachmentGroup extends AttachmentGroup {
  attachments: ServiceAttachment[];

  constructor(
    name: string,
    tag: string,
    type: ServiceAttachmentGroupType,
    sort?: number,
    attachments?: ServiceAttachment[],
  ) {
    super(name, tag, type, sort);
    this.attachments = attachments ?? [];
  }

  toMap(attachmentGroup: ServiceAttachmentGroup): Map<string, unknown> {
    const map = super.toMap(attachmentGroup);
    if (attachmentGroup.attachments.length > 0) {
      const attArray = [];
      attachmentGroup.attachments.forEach((c: ServiceAttachment) => {
        const att = new ServiceAttachment(
          c.name,
          c.renderTag,
          c.templateUuid,
          c.newPage,
          true,
          false,
          c.sort,
        );
        attArray.push(att.toMap(att));
      });
      map.set("attachments", attArray ?? []);
    }
    return map;
  }
}

export class InteractiveAttachmentGroup extends AttachmentGroup {
  attachments: ServiceAttachment[];
  minNoOfAtt: number;
  maxNoOfAtt: number;
  constructor(
    name: string,
    tag: string,
    minNoOfAtt: number,
    maxNoOfAtt: number,
    sort?: number,

    attachments?: ServiceAttachment[],
  ) {
    super(name, tag, ServiceAttachmentGroupType.ALL_OPTIONAL , sort);
    this.minNoOfAtt = minNoOfAtt;
    this.maxNoOfAtt = maxNoOfAtt;
    this.attachments = attachments ?? [];
  }

  toMap(attachmentGroup: InteractiveAttachmentGroup): Map<string, unknown> {
    const map = super.toMap(attachmentGroup);
    if (attachmentGroup.attachments.length > 0) {
      const attArray = [];
      attachmentGroup.attachments.forEach((c: ServiceAttachment) => {
        const att = new ServiceAttachment(
          c.name,
          c.renderTag,
          c.templateUuid,
          c.newPage,
          true,
          false,
          c.sort,
        );
        attArray.push(att.toMap(att));
      });
      map.set("minNoOfAtt", attachmentGroup.minNoOfAtt);
      map.set("maxNoOfAtt", attachmentGroup.maxNoOfAtt);
      map.set("attachments", attArray ?? []);
    }
    return map;
  }
}

export class DeviceAttachmentGroup extends AttachmentGroup {
  attachments: DeviceAttachment[];
  constructor(
    name: string,
    tag: string,
    type: ServiceAttachmentGroupType,
    sort?: number,
    attachments?: DeviceAttachment[],
  ) {
    super(name, tag, type, sort);
    this.attachments = attachments ?? [];
  }
  toMap(attachmentGroup: DeviceAttachmentGroup): Map<string, unknown> {
    const map = super.toMap(attachmentGroup);
    if (attachmentGroup.attachments.length > 0) {
      const attArray = [];
      attachmentGroup.attachments.forEach((c: DeviceAttachment) => {
        const att = new DeviceAttachment(
          c.name,
          c.productCategoryUuid,
          c.renderTag,
          c.templateUuid,
          c.newPage,
          attachmentGroup.type !== ServiceAttachmentGroupType.REQUIRED_ALL,
          false,
          c.sort,
        );
        attArray.push(att.toMap(att));
      });
      map.set("attachments", attArray ?? []);
    }
    return map;
  }
}

export class AttachmentGroupForm {
  name = new FormControl();
  renderTag = new FormControl();
  type = new FormControl();
  sort = new FormControl();

  constructor(attachmentGroup: AttachmentGroup) {
    if (attachmentGroup.name) {
      this.name.setValue(attachmentGroup.name);
    }
    if (attachmentGroup.renderTag) {
      this.renderTag.setValue(attachmentGroup.renderTag);
    }
    if (attachmentGroup.type != null) {
      this.type.setValue(attachmentGroup.type);
    } else {
      this.type.setValue(ServiceAttachmentGroupType.ALL_OPTIONAL);
    }
    if (attachmentGroup.sort != null) {
      this.sort.setValue(attachmentGroup.sort);
    } else {
      this.sort.setValue(0);
    }
    this.name.setValidators([Validators.required]);
    this.name.updateValueAndValidity();
    this.renderTag.setValidators([Validators.required]);
    this.renderTag.updateValueAndValidity();
  }
}

export class ServiceAttachmentGroupForm extends AttachmentGroupForm {
  attachments = new FormArray([], [Validators.required]);

  constructor(attachmentGroup: ServiceAttachmentGroup) {
    super(attachmentGroup);

    if (
      attachmentGroup.attachments != null &&
      attachmentGroup.attachments.length > 0
    ) {
      attachmentGroup.attachments.sort((a, b) =>
        a.sort > b.sort ? 1 : b.sort > a.sort ? -1 : 0,
      );
      attachmentGroup.attachments.forEach((c) => {
        this.attachments.push(
          new FormGroup(new ServiceAttachmentForm(c) as unknown),
        );
      });
    }
    this.type.setValidators([Validators.required]);
    this.type.updateValueAndValidity();
  }
}

export class InteractiveAttachmentGroupForm extends AttachmentGroupForm {
  minNoOfAtt = new FormControl<number>(1, [Validators.required, Validators.min(0)]);
  maxNoOfAtt = new FormControl<number>(1, [Validators.required, Validators.max(20)]);
  attachments = new FormArray([], [Validators.required]);
  constructor(attachmentGroup: InteractiveAttachmentGroup) {
    super(attachmentGroup);
    if (attachmentGroup.minNoOfAtt) {
      this.minNoOfAtt.setValue(attachmentGroup.minNoOfAtt);
    }
    if (attachmentGroup.maxNoOfAtt) {
      this.maxNoOfAtt.setValue(attachmentGroup.maxNoOfAtt);
    }
    this.minNoOfAtt.setValidators([Validators.required, Validators.min(0)]);
    this.minNoOfAtt.updateValueAndValidity();
    this.maxNoOfAtt.setValidators([Validators.required, Validators.max(20)]);
    this.maxNoOfAtt.updateValueAndValidity();
    if (
      attachmentGroup.attachments != null &&
      attachmentGroup.attachments.length > 0
    ) {
      attachmentGroup.attachments.sort((a, b) =>
        a.sort > b.sort ? 1 : b.sort > a.sort ? -1 : 0,
      );
      attachmentGroup.attachments.forEach((c) => {
        this.attachments.push(new FormGroup(new ServiceAttachmentForm(c)));
      });
    }
  }
}


export class DeviceAttachmentGroupForm extends AttachmentGroupForm {
  attachments = new FormArray([], [Validators.required]);
  constructor(attachmentGroup: DeviceAttachmentGroup) {
    super(attachmentGroup);
    if (
      attachmentGroup.attachments != null &&
      attachmentGroup.attachments.length > 0
    ) {
      attachmentGroup.attachments.sort((a, b) =>
        a.sort > b.sort ? 1 : b.sort > a.sort ? -1 : 0,
      );
      attachmentGroup.attachments.forEach((c) => {
        this.attachments.push(new FormGroup(new DeviceAttachmentForm(c)));
      });
    }
  }
}

export class FormDocument {
  name: string;
  sort?: number;
  document?: LibraryDocument;

  constructor(name: string, doc?: LibraryDocument, sort?: number) {
    this.name = name;
    this.document = doc;
    this.sort = sort ?? 0;
  }

  toMap(formDocument: FormDocument): Map<string, unknown> {
    const map = new Map<string, unknown>();
    map.set("name", formDocument.name);
    map.set("document", formDocument.document);
    map.set("sort", formDocument.sort ?? 0);
    return map;
  }
}

export class DescriptiveFormTemplate {
  uuid: string;
  title: string;
  active: boolean;

  constructor(uuid: string, title: string, active: boolean) {
    this.uuid = uuid;
    this.title = title;
    this.active = active;
  }
}

export class FormDocumentForm {
  name = new FormControl();
  sort = new FormControl();
  document = new FormControl();

  constructor(formDocument: FormDocument) {
    if (formDocument.name) {
      this.name.setValue(formDocument.name);
    }
    if (formDocument.document) {
      this.document.setValue(formDocument.document);
    }
    if (formDocument.sort !== undefined) {
      this.sort.setValue(formDocument.sort);
    }
    this.name.setValidators([Validators.required]);
    this.document.setValidators([Validators.required]);
    this.name.updateValueAndValidity();
    this.document.updateValueAndValidity();
  }
}
